import React from 'react';
import ImageUpload from '@/components/ui/custom/input/ImageUpload';
import SelectFilter from '@/components/filter/SelectFilter';
import { CourseFormatEnum } from '@/models/Course';
import CoursePath from '@/models/CoursePath';
import { bodyText, heading2 } from '@/styles/styles';
import { courseFormatsOptions } from '@/utils/courseFormatsTranslation';
import { courseDurationOptions } from '@/utils/courseDurationTranslation';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import CourseHeaderActions from '../actions/CourseBoxActions';
import CourseStatus from '../CourseStatus';
import TitleForm from '../TitleForm';
import { CourseHeaderFields } from './CourseBoxHeaderForm';

type CourseBoxHeaderExpandedProps = {
  coursePath: CoursePath;
  isLoading?: boolean;
};

const CourseBoxHeaderExpanded = React.memo(function CourseBoxHeaderExpanded({
  coursePath,
}: CourseBoxHeaderExpandedProps) {
  const { control, watch, setValue } = useFormContext<CourseHeaderFields>();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseBox.form',
  });
  const formatsOptions = courseFormatsOptions();
  const durationOptions = courseDurationOptions();

  const [format, duration] = watch(['course.formats', 'course.duration']);

  const { field } = useController({
    control,
    name: 'course.bannerImg',
  });

  return (
    <div className="flex gap-2.5 justify-between w-full">
      <section className="flex gap-2.5 w-full">
        <ImageUpload
          accept=".jpg,.jpeg,.png,.gif,.webp"
          value={field.value}
          onChange={field.onChange}
          className={{
            div: 'w-[14rem] h-full',
            base: 'w-fit',
            img: 'rounded-lg',
          }}
        />
        <div className="flex flex-col gap-2.5 w-full">
          <TitleForm
            control={control}
            maxLength={200}
            fieldName="course.name"
            className={{
              span: twJoin(
                heading2,
                'text-primary !font-poppins truncate lg:max-w-[350px] xl:max-w-[600px]',
              ),
              icon: 'w-5 h-5',
            }}
          />
          <SelectFilter
            name="course.format"
            label={t('format')}
            options={formatsOptions}
            className={{
              text: 'text-secondary font-500 text-[0.875rem]',
            }}
            value={format?.[0]}
            showOnClear={false}
            type="select"
            setValue={(_, value) =>
              setValue('course.formats', [value] as CourseFormatEnum[])
            }
          />
          <SelectFilter
            name="course.duration"
            label={t('duration')}
            options={durationOptions}
            className={{
              text: 'text-secondary font-500 text-[0.875rem]',
            }}
            value={duration ?? undefined}
            type="select"
            showOnClear={false}
            setValue={(_, value) =>
              setValue('course.duration', value as number)
            }
          />
          <TitleForm
            control={control}
            fieldName="course.abbreviation"
            className={{
              span: twJoin(bodyText, 'text-secondary font-500'),
              icon: 'text-accent',
            }}
            maxLength={7}
            label={{ text: t('abbreviation'), className: 'font-400' }}
          />
        </div>
      </section>
      <div className="flex flex-col gap-2.5 justify-between items-end mt-4 w-full">
        <CourseHeaderActions coursePath={coursePath} />
        <CourseStatus status={coursePath.status} version={coursePath.version} />
      </div>
    </div>
  );
});

export default CourseBoxHeaderExpanded;
